import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { parse } from "wkt";
import { DialogHeader, DialogLabel, DialogValue, FloatTools, Preview, ResultDialogContainer, ValueCard, } from "./styled";
import { Button, Flex, FlexSpan, Icon, IconButton } from "../../../../ui";
import { DIALOGS } from "../../../../constants/dialogs";
import { EMPTY_VALUE } from "../../../../constants/common";
import { useDialog, useSites, useUser } from "../../../../hooks";
import { getFeatureOnPoint } from "../../../../utils/getFeatureOnPoint";
import { copyToClipboard } from "../../../../utils/copyToClipboard";
import { api, EventTypes } from "../../../../api";
// eslint-disable-next-line max-lines-per-function
export const ClickResultDialog = ({ feature, address }) => {
    const openDialog = useDialog();
    const [sites, setSites] = useSites();
    const [user] = useUser();
    const [currentSite, setCurrentSite] = useState();
    const [linkPoint, setLinkPoint] = useState();
    const [linkCopied, setLinkCopied] = useState(false);
    useEffect(() => {
        setLinkPoint(getFeatureOnPoint(feature)?.geometry.coordinates.join(","));
    }, [feature]);
    const featureGeoJson = useMemo(() => {
        if (!feature) {
            return {};
        }
        return {
            type: feature.type,
            properties: {
                fill: "#DDE0FF",
                "stroke-width": 2,
            },
            geometry: parse(feature.properties.GEOM),
        };
    }, [feature]);
    const onToggleLike = useCallback(() => {
        if (!user) {
            openDialog(DIALOGS.LOGIN_DIALOG);
            return;
        }
        if (!currentSite) {
            return;
        }
        api.userSites
            .updateUserSite(currentSite.id, {
            is_favorite: !currentSite.is_favorite,
        })
            .then(response => {
            setSites(response);
            setCurrentSite(response.find(site => JSON.parse(site.feature).id === currentSite.id));
        });
    }, [user, currentSite, openDialog, setSites]);
    useEffect(() => {
        if (!feature || !sites) {
            return;
        }
        const savedSite = sites.find(site => JSON.parse(site.feature)?.id === feature.id);
        if (savedSite) {
            setCurrentSite(savedSite);
        }
        else if (user) {
            api.userSites
                .createUserSite({
                feature: JSON.stringify({
                    ...feature,
                    geometry: parse(feature.properties.GEOM),
                }),
            })
                .then(response => {
                setSites(response);
                setCurrentSite(response[0]);
            });
        }
        setLinkCopied(false);
    }, [feature]); // eslint-disable-line
    useEffect(() => {
        if (!feature || !sites) {
            return;
        }
        setCurrentSite(sites.find(site => JSON.parse(site.feature)?.id === feature.id));
    }, [sites]); // eslint-disable-line
    return (_jsx(ResultDialogContainer, { children: _jsxs(Flex, { column: true, gap: "1rem", height: "auto", maxHeight: "calc(var(--app-height) - 13rem)", children: [_jsx(FlexSpan, { height: "auto", maxHeight: "calc(100% - 3.25rem)", overflowY: "auto", children: _jsxs(Flex, { column: true, gap: "1rem", children: [_jsx(FlexSpan, { children: _jsxs(DialogHeader, { children: [_jsxs(FlexSpan, { minWidth: "50%", children: [_jsx(FloatTools, { children: _jsx(FlexSpan, { children: _jsxs(Flex, { alignItems: "center", gap: "0.5rem", children: [_jsx(FlexSpan, { children: _jsx(IconButton, { icon: "like", onClick: onToggleLike, active: currentSite?.is_favorite }) }), _jsx(FlexSpan, { children: _jsx(IconButton, { icon: linkCopied ? "confirm" : "link", size: 16, onClick: async () => {
                                                                            await api.events.createEvent({
                                                                                user_id: user.id,
                                                                                type: EventTypes.CopySiteLink,
                                                                                description: `${window.location.origin}/?c=${linkPoint ?? ""}`,
                                                                            });
                                                                            copyToClipboard(`${window.location.origin}/?c=${linkPoint ?? ""}`, true).then(() => {
                                                                                setLinkCopied(true);
                                                                            });
                                                                        } }) })] }) }) }), _jsx(Preview, { imageSrc: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/geojson(${encodeURI(JSON.stringify(featureGeoJson)).replace(/#/, "%23")})/auto/800x290@2x?attribution=false&logo=false&padding=100,250,100,250&access_token=${process.env.ACCESS_TOKEN}` })] }), _jsx(FlexSpan, { children: _jsxs(Flex, { column: true, gap: "1rem", children: [_jsx(FlexSpan, { children: _jsxs(Flex, { column: true, gap: "0.25rem", children: [_jsx(FlexSpan, { children: _jsx(DialogLabel, { children: "Address" }) }), _jsx(FlexSpan, { children: _jsx(DialogValue, { children: address }) })] }) }), _jsx(FlexSpan, { children: _jsxs(Flex, { column: true, gap: "0.25rem", children: [_jsx(FlexSpan, { children: _jsx(DialogLabel, { children: "Site ID" }) }), _jsx(FlexSpan, { children: _jsx(DialogValue, { children: feature?.properties.PARCEL_ID ?? EMPTY_VALUE }) })] }) })] }) })] }) }), _jsx(FlexSpan, { children: _jsxs(Flex, { justifyContent: "space-between", gap: "0.5rem", children: [_jsx(FlexSpan, { width: "calc(100% / 3 - 0.25rem)", height: "fit-content", borderRadius: "0.75rem", backgroundColor: "#F6F5F8", p: "0.5rem", children: _jsxs(ValueCard, { children: [_jsx(DialogLabel, { children: "Site Area" }), _jsxs(DialogValue, { children: [feature?.properties.LEGAL_AREA ?? EMPTY_VALUE, " m", _jsx("sup", { children: "2" })] })] }) }), _jsx(FlexSpan, { width: "calc(100% / 3 - 0.25rem)", height: "fit-content", borderRadius: "0.75rem", backgroundColor: "#F6F5F8", p: "0.5rem", children: _jsxs(ValueCard, { children: [_jsx(DialogLabel, { children: "Ownership" }), _jsx(DialogValue, { children: feature?.properties.OWNER_T ?? EMPTY_VALUE })] }) }), _jsx(FlexSpan, { width: "calc(100% / 3 - 0.25rem)", height: "fit-content", borderRadius: "0.75rem", backgroundColor: "#F6F5F8", p: "0.5rem", children: _jsxs(ValueCard, { children: [_jsx(DialogLabel, { children: "Use Zone" }), _jsx(DialogValue, { children: feature?.properties.ZONE_DESC ?? EMPTY_VALUE })] }) })] }) })] }) }), _jsx(FlexSpan, { height: "3.25rem", children: _jsx(FlexSpan, { mt: "1rem", children: _jsx(Flex, { column: true, gap: "0.5rem", children: _jsx(FlexSpan, { children: _jsxs(Button, { width: "100%", onClick: async () => {
                                        if (!user) {
                                            openDialog(DIALOGS.LOGIN_DIALOG);
                                            return;
                                        }
                                        if (currentSite) {
                                            await api.events.createEvent({
                                                user_id: user.id,
                                                type: EventTypes.DownloadSiteReport,
                                                description: address,
                                            });
                                            window.open(`${process.env.API_URL}/reports/download/${currentSite.id}`, "_top");
                                        }
                                    }, children: [_jsx(Icon, { icon: "download", mr: "1rem" }), "Download report"] }) }) }) }) })] }) }));
};
